import {
    Component,
    EventEmitter,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import {
    AccumulationTheme,
    CategoryService,
    ChartAnnotationService,
    ChartComponent,
    ColumnSeriesService,
    DataLabelService,
    DateTimeService,
    LegendService,
    LineSeriesService,
    RangeColumnSeriesService,
    ScrollBarService,
    SelectionService,
    StackingColumnSeriesService,
    TooltipService,
    ZoomService
} from '@syncfusion/ej2-angular-charts';
import { DashboardLayoutComponent } from '@syncfusion/ej2-angular-layouts';
import { Browser } from '@syncfusion/ej2-base';
import { AnimationModel } from '@syncfusion/ej2-progressbar';
import {
    endOfMonth,
    endOfWeek,
    endOfYear,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subYears,
} from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { take } from 'rxjs/operators';
import { SalesDisplayMode } from 'src/app/enums/sales.display.mode';
import { SessionUser } from 'src/app/model/session.user';
import { CommonService } from 'src/app/services/common.service';
import { CompanyService } from 'src/app/services/company.service';
import { DashboardService } from 'src/app/services/dashboard.service';
import { LoginService } from 'src/app/services/login.service';
import { ChartUtils } from 'src/app/utils/chart-util';
import { transformDateToApiFormat } from 'src/app/utils/date-util';
import { getDashboardParams } from 'src/app/utils/grid-util';

@Component({
    selector: 'app-cashback-graph',
    templateUrl: './cashback-graph.component.html',
    styleUrl: './cashback-graph.component.css',
    providers: [
        CategoryService,
        DateTimeService,
        ScrollBarService,
        LineSeriesService,
        ColumnSeriesService,
        ChartAnnotationService,
        RangeColumnSeriesService,
        StackingColumnSeriesService,
        LegendService,
        TooltipService,
        ZoomService,
        DataLabelService,
        ZoomService,
        SelectionService,
    ],
})
export class CashbackGraphComponent implements OnInit {
    @ViewChild('defaultLayout') public dashboard: DashboardLayoutComponent;
    @ViewChild('cvdChart') public cvdChart: ChartComponent; // Sales vs Date Chart
    @ViewChild('cashbackByProvincePieChart') public cashbackByProvincePieChart: ChartComponent; // Sales by Province Pie Chart
    @ViewChild('productChart') public productChart?: ChartComponent; // Sales by Product Chart
    @ViewChild('storeChart') public salesPerStoreChart?: ChartComponent; // Sales by Product Chart
    @Output() submitSearch = new EventEmitter<void>();
    isClearingFilters: boolean = false;

    filters = {
        businessUnits: [],
        provinces: [],
        wholesalers: [],
        products: [],
        stores: [],
        suppliers: [],
        timeRange: { from: '', to: '' },
    };
    isExporting: boolean = false;
    // Chart dimensions
    public widthValue = '100%';
    public heightValue = '100%';

    // Sales vs Date (SVD) Chart properties
    public svdPrimaryXAxis?: Object;
    public svdPrimaryYAxis?: Object;
    public svdChartData?: Object[] = [];
    public svdTooltip?: Object;
    public svdLegendSettings?: Object;
    public svdColumnSeriesFillColour?: string;

    // Sales by Province Pie Chart properties
    public center?: Object;
    public pieChartData?: Object[] = [];
    public pieDataLabel?: Object;
    public explode = false;
    public pieChartLegendSettings?: Object;
    public enableSmartLabel = true;
    public startAngle = 0;
    public endAngle = 360;
    public aspectRatio?: any;

    // Sales by Product Column Chart properties
    public salesProductList: Object[] = [];
    public salesStoreList: Object[] = [];

    public productXAxis?: Object = {};
    public productYAxis?: Object = {};

    public storeXAxis?: Object = {};
    public storeYAxis?: Object = {};

    public cellSpacing: number[] = [10, 10];
    public columns = '3';
    public allowDragging = false;

    public animation: AnimationModel = { enable: true, duration: 2000, delay: 0 };
    public isIndeterminate = true;
    public progressBarType = 'Circular';
    public progressBarHeight = '60';
    public progressBarValue = 20;
    public progressColor = '#ee8031';

    // Filter lists
    productList: any[] = [];
    storeList: any[] = [];
    wholesalerList: any[] = [];
    businessUnitList: any[] = [];
    provinceList: any[] = [];
    supplierList: any[] = [];

    form = this.formBuilder.group({
        productIds: new FormControl([]),
        wholesalerIds: new FormControl([]),
        storeIds: new FormControl([]),
        supplierBusinessUnitIds: new FormControl([]),
        supplierIds: new FormControl([]),
        provinceIds: new FormControl([]),
        fromDate: new FormControl(''),
        toDate: new FormControl(''),
        fromDateFilter: new FormControl(''),
        toDateFilter: new FormControl(''),
        view: new FormControl('cashback'),
    });

    // Filter related flags and objects
    showAdvancedFilters = false;
    supplierFieldKey = 'supplierIds';
    productFieldKey = 'productIds';
    businessFieldKey = 'supplierBusinessUnitIds';
    provinceFieldKey = 'provinceIds';
    wholesalerFieldKey = 'wholesalerIds';
    storeFieldKey = 'storeIds';
    searchItemsObj: { [key: string]: string[] } = {};
    searchItems: { [key: string]: string[] } = {};

    presetRanges: any = {};
    dateRange = { startDate: new Date(), endDate: new Date() };
    activeTab: 'sales' | 'cashback' = 'sales';
    user: SessionUser;
    sessionUser$ = this.loginService.sessionUser.asObservable();
    public primaryXAxis?: Object;
    public chartData?: Object[];
    public title?: string;
    primaryYAxis: any;
    chartReady = false;
    isBusy: boolean = false;
    viewDateAs: SalesDisplayMode = SalesDisplayMode.daily
    chartUtils = ChartUtils;

    constructor(
        public companyService: CompanyService,
        private dashboardService: DashboardService,
        public loginService: LoginService,
        private formBuilder: FormBuilder,
        private commonService: CommonService,
    ) { }

    ngOnInit(): void {
        this.loginService.sessionUser.pipe(take(1)).subscribe((user) => {
            this.user = user;
            this.initializeDateRange();
            this.setupDashboard();
            this.setupCharts();
            this.getData(true);
        });
    }

    initializeDateRange(): void {
        const today = new Date();
        const yesterday = subDays(today, 1);
        const startWeek = startOfWeek(today, { weekStartsOn: 1 });
        const endWeek = endOfWeek(today, { weekStartsOn: 1 });

        this.presetRanges = {
            todayStart: today,
            todayEnd: today,
            yesterdayStart: yesterday,
            yesterdayEnd: yesterday,
            weekStart: startWeek,
            weekEnd: endWeek,
            lastWeekStart: subDays(startWeek, 7),
            lastWeekEnd: subDays(endWeek, 7),
            monthStart: startOfMonth(today),
            monthEnd: endOfMonth(today),
            lastMonthStart: startOfMonth(subMonths(today, 1)),
            lastMonthEnd: endOfMonth(subMonths(today, 1)),
            yearStart: startOfYear(today),
            yearEnd: endOfYear(today),
            lastYearStart: startOfYear(subYears(today, 1)),
            lastYearEnd: endOfYear(subYears(today, 1)),
        };

        this.form.patchValue({
            fromDateFilter: this.presetRanges.weekStart,
            toDateFilter: this.presetRanges.weekEnd,
            fromDate: transformDateToApiFormat(this.presetRanges.weekStart),
            toDate: transformDateToApiFormat(this.presetRanges.weekEnd),
        });
    }

    setupCharts(): void {
        this.setupSvdChart();
        this.setupPieChart();
        this.setupProductChart();
        this.setupStoreChart();
    }

    toggleOption() {
        this.showAdvancedFilters = !this.showAdvancedFilters;
    }

    /**
     * Sets the active tab (sales or cashback).
     * @param tab The tab to set as active.
     */
    setActiveTab(tab: 'sales' | 'cashback') {
        this.activeTab = tab;
    }

    submit() {
        if (!this.isClearingFilters) {
            this.getData(false);
        }
    }

    clearFilters() {
        this.isClearingFilters = true;
        this.searchItems = {};
        this.searchItemsObj = {};
        const defaultFromDate = transformDateToApiFormat(this.presetRanges.weekStart);
        const defaultToDate = transformDateToApiFormat(this.presetRanges.weekEnd);

        this.form.get('productIds')?.setValue([]);
        this.form.get('wholesalerIds')?.setValue([]);
        this.form.get('supplierBusinessUnitIds')?.setValue([]);
        this.form.get('provinceIds')?.setValue([]);
        this.form.get('storeIds')?.setValue([]);
        this.form.get('supplierIds')?.setValue([]);

        this.form.patchValue({
            fromDateFilter: this.presetRanges.weekStart,
            toDateFilter: this.presetRanges.weekEnd,
            fromDate: defaultFromDate,
            toDate: defaultToDate,
        });
        setTimeout(() => {
            this.isClearingFilters = false;
            this.getData(false);
        }, 1000);
    }

    /**
     * Handles the change event for filter selections.
     * @param event The event object containing selected items.
     * @param key The key representing the filter type.
     */
    onChange(event: any, key: string) {
        let labelKey = 'name';
        if (key === this.businessFieldKey) labelKey = this.getBusinessFilterName();
        if (key === this.productFieldKey) labelKey = 'groupTitle';

        if (event && event.length > 0) {
            this.searchItemsObj[key] = event.map((e: any) => e[labelKey]);
        } else {
            this.form.get(key)?.setValue([]);
            delete this.searchItemsObj[key];
            delete this.searchItems[key];
        }
    }

    /**
     * Handles clearing a specific filter.
     * @param key The key of the filter to clear.
     */
    onClear(key: string) {
        this.doRemove([], '', key, '', '');
        if (!this.isClearingFilters) {
            this.getData(false);
        }
    }

    /**
     * Removes a specific item from a filter.
     * @param key The key of the filter.
     * @param value The value to remove.
     */
    removeSearchItem(key: string, value: string) {
        const filterMapping: { [key: string]: { list: any[]; label: string; valKey: string } } = {
            [this.supplierFieldKey]: { list: this.supplierList, label: 'name', valKey: 'id' },
            [this.productFieldKey]: { list: this.productList, label: 'groupTitle', valKey: 'groupCode' },
            [this.storeFieldKey]: { list: this.storeList, label: 'name', valKey: 'id' },
            [this.businessFieldKey]: { list: this.businessUnitList, label: this.getBusinessFilterName(), valKey: 'id' },
            [this.provinceFieldKey]: { list: this.provinceList, label: 'name', valKey: 'id' },
            [this.wholesalerFieldKey]: { list: this.wholesalerList, label: 'name', valKey: 'id' },
        };

        const mapping = filterMapping[key];
        if (mapping) {
            this.doRemove(mapping.list, value, key, mapping.label, mapping.valKey);
            this.getData(false);
        }
    }

    /**
     * Performs the actual removal of a filter item.
     * @param list The list of available filter options.
     * @param value The value to remove.
     * @param key The key of the filter.
     * @param labelKey The key for the label in the filter item.
     * @param valKey The key for the value in the filter item.
     */
    doRemove(list: any[], value: string, key: string, labelKey: string, valKey: string) {
        this.searchItemsObj[key] = this.searchItemsObj[key]?.filter((v) => v !== value) || [];

        const updated = list.reduce((arr: any[], item: any) => {
            if (this.searchItemsObj[key]?.includes(item[labelKey])) {
                arr.push(item[valKey]);
            }
            return arr;
        }, []);

        this.form.get(key)?.setValue(updated);

        if (this.searchItemsObj[key]?.length < 1) {
            delete this.searchItemsObj[key];
            delete this.searchItems[key];
        }
    }

    /**
     * Returns the appropriate business filter name based on user role.
     * @returns The business filter name.
     */
    getBusinessFilterName(): string {
        return this.user?.isAdmin() ? 'displayName' : 'name';
    }

    /**
     * Constructs dashboard parameters from the form values.
     * @returns Dashboard parameters object.
     */
    getDashboardParams() {
        this.processDates();
        return getDashboardParams(this.form, this.user, true);
    }

    processDates() {
        const fromDate = this.form.get('fromDateFilter')?.value;
        const toDate = this.form.get('toDateFilter')?.value;

        if (fromDate) {
            const formattedFromDate = transformDateToApiFormat(fromDate);
            this.form.get('fromDate')?.setValue(formattedFromDate);
        } else {
            this.form.get('fromDate')?.setValue(null);
        }

        if (toDate) {
            const formattedToDate = transformDateToApiFormat(toDate);
            this.form.get('toDate')?.setValue(formattedToDate);
        } else {
            this.form.get('toDate')?.setValue(null);
        }
    }

    /**
     * Fetches sales data from the API and updates chart data.
     * @param componentInit A boolean indicating if this is called during component initialization.
     */
    getData(componentInit = true) {
        this.setBusyState(true);
        this.chartReady = false;
        const params = this.getDashboardParams();

        this.dashboardService.getFitlersData(params).subscribe(
            (res) => {
                if (res.result) {
                    let filters = res?.result
                    this.productList = filters['products'] || [];
                    this.storeList = filters['stores'] || [];
                    this.wholesalerList = filters['wholesalers'] || [];
                    this.businessUnitList = filters['supplierBusinessUnits'] || [];
                    this.provinceList = filters['provinces'] || [];
                    this.supplierList = filters['suppliers'] || [];

                    if (
                        this.user?.isEnterprise() &&
                        (!this.businessUnitList || this.businessUnitList.length === 0)
                    ) {
                        const companyName =
                            this.user.enterprise?.businessName || 'Default Enterprise Name';
                        const enterpriseBuId = `enterprise_${this.user.enterprise?.enterpriseId || 'default_id'}_bu`;

                        this.businessUnitList = [
                            {
                                id: enterpriseBuId,
                                name: companyName,
                                displayName: companyName,
                            },
                        ];
                    }

                    this.filters = {
                        ...this.filters,
                        products: this.productList,
                        stores: this.storeList,
                        businessUnits: this.businessUnitList,
                        provinces: this.provinceList,
                        wholesalers: this.wholesalerList,
                        suppliers: this.supplierList,
                    };
                }

                this.setBusyState(false);
            },
            (error) => {
                this.setBusyState(false);
                this.svdChartData = [];
                this.pieChartData = [];
                this.salesProductList = [];
            },
        );

        this.dashboardService.getGraphData(params).subscribe(
            (res) => {
                if (res?.responseBody?.data) {
                    this.viewDateAs = res.responseBody.data['viewDateAs'];
                    this.svdChartData = this.processCashbackByDate(
                        res.responseBody.data['cashbackByDate'] || [],
                    );
                    this.pieChartData = res.responseBody.data['cackbackByProvince'] || [];
                    this.salesProductList = this.processCashbackByProduct(
                        res.responseBody.data['cashbackByProduct'] || [],
                    );
                    this.salesStoreList = this.processCashbackByStore(
                        res.responseBody.data['cackbackByStore'] || [],
                    );
                    this.chartReady = true;
                    if (componentInit) {
                        setTimeout(() => {
                            this.refreshCharts();
                        }, 0);
                    } else {
                        this.refreshCharts();
                    }

                    for (const key in this.searchItemsObj) {
                        if (this.form.get(key)?.value?.length > 0) {
                            this.searchItems[key] = this.searchItemsObj[key];
                        }
                    }
                } else {
                    console.error('Invalid data response structure:', res);
                }


                this.setBusyState(false);
            },
            (error) => {
                this.setBusyState(false);
                this.resetCharts();
            },
        );
    }

    resetCharts() {
        this.svdChartData = [];
        this.pieChartData = [];
        this.salesProductList = [];
        this.salesStoreList = [];
        this.chartReady = false;
    }

    refreshCharts() {
        setTimeout(() => {
            requestAnimationFrame(() => {
                if (this.cvdChart) {
                    this.setupSvdChart();
                    this.cvdChart.refresh();
                }

                if (this.cashbackByProvincePieChart) {
                    this.cashbackByProvincePieChart.refresh();
                }

                if (this.productChart) {
                    this.productChart.refresh();
                }

                if (this.salesPerStoreChart) {
                    this.salesPerStoreChart.refresh();
                }
            });
        }, 100);
    }

    setBusyState(val: boolean) {
        this.isBusy = val;
    }

    /**
     * Processes sales by date data for the column chart.
     * @param cashbackData Raw sales data.
     * @returns Processed sales data for the chart.
     */
    processCashbackByDate(cashbackData: any[]) {
        if (!cashbackData || !Array.isArray(cashbackData) || cashbackData.length === 0) {
            return [];
        }

        return cashbackData.map((value: any) => ({
            sales: value.value || 0, // Use 'value' from API for sales
            date: this.viewDateAs === SalesDisplayMode.weekly
                ? String(value.date || '') // 'Week 1', etc.
                : value.date ? new Date(value.date) : new Date() // actual date
        }));
    }

    /**
     * Processes sales by product data for the column chart.
     * Maps 'name' to 'productName' and 'value' to 'sales'.
     * @param cashbackData Raw sales by product data.
     * @returns Processed sales data for the product chart.
     */
    processCashbackByProduct(cashbackData: any[]) {
        if (!cashbackData || !Array.isArray(cashbackData) || cashbackData.length === 0) {
            return [];
        }

        return cashbackData.map((item: any) => ({
            productName: item.name,
            sales: item.value || 0,
        }));
    }

    processCashbackByStore(cashbackData: any[]) {
        if (!cashbackData || !Array.isArray(cashbackData) || cashbackData.length === 0) {
            return [];
        }

        return cashbackData.map((item: any) => ({
            storeName: item.name,
            sales: item.value || 0,
        }));
    }

    setupDashboard() {
        this.cellSpacing = [10, 10];
        this.columns = '3';
    }

    setupSvdChart(): void {
        this.svdColumnSeriesFillColour = '#de2b6a';
        this.svdTooltip = { enable: true, format: 'R ${point.y: n0}' };
        this.svdLegendSettings = { visible: false };
        this.svdPrimaryXAxis = {
            title: 'Date',
            valueType: this.viewDateAs === SalesDisplayMode.weekly ? 'Category' : 'DateTimeCategory',
            labelFormat: this.chartUtils.getSvdLabelFormat(this.viewDateAs),
            majorGridLines: { width: 0 },
            intervalType: this.chartUtils.getSvdIntervalType(this.viewDateAs),
            edgeLabelPlacement: 'Shift',
            labelIntersectAction: Browser.isDevice ? 'None' : 'Trim',
            labelRotation: Browser.isDevice ? -45 : 0,
            majorTickLines: { width: 0 },
            minorTickLines: { width: 0 },
            zoomFactor: 1,
            zoomPosition: 0
        };
        this.svdPrimaryYAxis = {
            title: 'Sales (R)',
            labelFormat: '{value}',
            lineStyle: { width: 0 },
            majorTickLines: { width: 0 },
            minorTickLines: { width: 0 },
            valueType: 'Double',
            rangePadding: 'Auto',
        };
    }

    setupPieChart(): void {
        this.center = { x: '50%', y: '50%' };
        this.pieChartLegendSettings = { visible: true };
        this.enableSmartLabel = true;
        this.aspectRatio = 100 / 85;
        this.pieDataLabel = {
            connectorStyle: { length: '30px', type: 'Curve' },
            visible: true,
            position: 'Outside',
            name: 'name',
            font: { fontWeight: '600', fontFamily: 'Roboto' },
            textWrap: 'Wrap',
            maxWidth: 100,
            template:
                "<div id='dataLabelTemplate'>${point.x} (R ${point.y})<br>" +
                "<div class='percentage'>${point.percentage}%</div></div>",
        };
    }

    public zoomSettings: Object = {
        mode: 'X',
        enableMouseWheelZooming: true,
        enablePinchZooming: false,
        enableSelectionZooming: false,
        enableScrollbar: true,
        toolbarItems: ['ZoomIn', 'ZoomOut', 'Reset'],
        showToolbar: true
    };

    setupProductChart(): void {
        this.productXAxis = {
            title: 'Products',
            valueType: 'Category',
            labelRotation: Browser.isDevice ? -45 : -30,
            majorGridLines: { width: 0 },
            labelStyle: {
                textOverflow: 'Ellipsis',
                maxWidth: 100,
            },
            zoomFactor: 1,
            zoomPosition: 0
        };
        this.productYAxis = {
            title: 'Cashback (R)',
            labelFormat: 'R{value}',
            lineStyle: { width: 0 },
            majorTickLines: { width: 0 },
            minorTickLines: { width: 0 },
            valueType: 'Double',
            rangePadding: 'Auto',
        };
    }

    setupStoreChart(): void {
        this.storeXAxis = {
            title: 'Stores',
            valueType: 'Category',
            labelRotation: Browser.isDevice ? -45 : -30,
            majorGridLines: { width: 0 },
            labelStyle: {
                textOverflow: 'Ellipsis',
                maxWidth: 100,
            },
            zoomFactor: 1,
            zoomPosition: 0
        };
        this.storeYAxis = {
            title: 'Cashback (R)',
            labelFormat: 'R{value}',
            lineStyle: { width: 0 },
            majorTickLines: { width: 0 },
            minorTickLines: { width: 0 },
            valueType: 'Double',
            rangePadding: 'Auto',
        };
    }

    /**
     * Handles chart load event to apply theme.
     * @param args Chart event arguments.
     */
    chartLoad(args: any): void {
        args.chart.theme = <any>(
            (this.getTheme().charAt(0).toUpperCase() + this.getTheme().slice(1))
                .replace(/-dark/i, 'Dark')
                .replace(/contrast/i, 'Contrast')
        );
    }

    /**
     * Handles pie chart load event to apply theme.
     * @param args Accumulation chart event arguments.
     */
    pieChartLoad(args: any): void {
        args.accumulation.theme = <AccumulationTheme>(
            (this.getTheme().charAt(0).toUpperCase() + this.getTheme().slice(1)).replace(
                /-dark/i,
                'Dark',
            )
        );
    }

    /**
     * Gets the current theme from the URL hash.
     * @returns The current theme name.
     */
    getTheme() {
        const selectedTheme: string = location.hash.split('/')[1];
        return selectedTheme ? selectedTheme : 'Material';
    }

    /**
     * Handles date range change event from the filter component.
     * @param event The date range event object.
     */
    onRangeChange(event: any) {
        if (event && event.startDate && event.endDate) {
            this.form.patchValue({
                fromDateFilter: event.startDate,
                toDateFilter: event.endDate,
            });
            this.submit();
        } else {
            console.warn('Invalid date range event:', event);
        }
    }

    exportChartsToPDF(): void {
        this.isExporting = true;
        setTimeout(() => {
            const chartsAndTitles = [
                { id: 'cvdChartContainer', title: 'Cashback vs Date' },
                { id: 'cashbackByProvince', title: 'Cashback By Province' },
                { id: 'cashbackByProductChart', title: 'Cashback By Product' },
                { id: 'cashbackByStoreChart', title: 'Cashback By Store' },
            ];

            const pdf = new jsPDF('p', 'mm', 'a4');
            let yPosition = 15;
            const leftMargin = 10;
            const titleFontSize = 14;
            const titleSpacing = 8;
            const chartSpacing = 15;
            const pageHeight = pdf.internal.pageSize.getHeight();
            const pageWidth = pdf.internal.pageSize.getWidth();

            const addChartToPDFRecursive = (index: number) => {
                this.isExporting = true;
                if (index >= chartsAndTitles.length) {
                    pdf.save('cashback-dashboard-charts.pdf');
                    this.isExporting = false;
                    return;
                }

                const chartInfo = chartsAndTitles[index];
                const chartElement = document.getElementById(chartInfo.id);

                if (!chartElement) {
                    addChartToPDFRecursive(index + 1);
                    return;
                }

                if (index === 3) {
                    pdf.addPage();
                    yPosition = 15;
                }

                const html2canvasOptions = {
                    scale: 2, // Higher resolution
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#ffffff',
                    width: chartElement.offsetWidth,
                    height: chartElement.offsetHeight,
                };

                html2canvas(chartElement as HTMLElement, html2canvasOptions)
                    .then((canvas) => {
                        const imgData = canvas.toDataURL('image/png', 1.0);
                        const imgProps = pdf.getImageProperties(imgData);

                        const maxWidth = pageWidth - 2 * leftMargin;
                        const maxHeight = pageHeight - yPosition - 30;

                        let pdfWidth = maxWidth;
                        let pdfHeight = (imgProps.height * maxWidth) / imgProps.width;

                        if (pdfHeight > maxHeight) {
                            pdfHeight = maxHeight;
                            pdfWidth = (imgProps.width * maxHeight) / imgProps.height;
                        }
                        if (index !== 3) {
                            const totalRequiredHeight =
                                titleFontSize + titleSpacing + pdfHeight + chartSpacing;

                            if (yPosition + totalRequiredHeight > pageHeight - 20) {
                                pdf.addPage();
                                yPosition = 15;
                            }
                        }

                        pdf.setFontSize(titleFontSize);
                        pdf.setFont('helvetica', 'bold');
                        pdf.text(chartInfo.title, leftMargin, yPosition);
                        yPosition += titleSpacing;

                        pdf.addImage(imgData, 'PNG', leftMargin, yPosition, pdfWidth, pdfHeight);
                        yPosition += pdfHeight + chartSpacing;

                        addChartToPDFRecursive(index + 1);
                    })
                    .catch((error) => {
                        addChartToPDFRecursive(index + 1);
                    });
            };
            addChartToPDFRecursive(0);
        }, 0);
    }

    sumSales(data?: any[]): number {
        return data?.reduce((sum, item) => sum + ((item as any).sales || 0), 0) || 0;
    }
}
